import {
  AnalyticsDeductionDimension,
  IDeductionsByTypeAndReasonRow,
  ScoreCardTrend,
} from "@/core/repositories/analytics";
import { MAX_TOP_ANALYTICS_ROWS, MIN_ANALYTICS_SHARE_PERCENT } from "./constants";

export const OTHER_ROW_LABEL = "Others";

export interface DeductionByTypeTableRow {
  id: string;
  label: string;
  amount: number;
  count: number;
  share: number;
  vsPrior: number | null;
  trend: ScoreCardTrend | null;
  isOther: boolean;
}

const isUnassignedTypeRow = (row: IDeductionsByTypeAndReasonRow): boolean => {
  const deductionType = row.deductionType;
  if (!deductionType) return true;

  const name = deductionType.deductionName;
  return name == null || name === "";
};

const isUnassignedGlCodeRow = (row: IDeductionsByTypeAndReasonRow): boolean => {
  const glCode = row.glCode;
  if (!glCode) return true;

  const code = glCode.glCode;
  const description = glCode.glCodeDescription;
  const hasCode = code != null && code !== "";
  const hasDescription = description != null && description !== "";

  return !hasCode && !hasDescription;
};

const isUnassignedRow = (row: IDeductionsByTypeAndReasonRow, dimension: AnalyticsDeductionDimension): boolean =>
  dimension === "TYPE" ? isUnassignedTypeRow(row) : isUnassignedGlCodeRow(row);

const formatTypeLabel = (row: IDeductionsByTypeAndReasonRow): string => row.deductionType!.deductionName!;

const formatGlCodeLabel = (row: IDeductionsByTypeAndReasonRow): string => {
  const glCode = row.glCode!;
  const code = glCode.glCode;
  const description = glCode.glCodeDescription;

  if (code != null && code !== "" && description != null && description !== "") {
    return `${code} - ${description}`;
  }
  if (code != null && code !== "") {
    return String(code);
  }
  return String(description);
};

const getRowLabel = (row: IDeductionsByTypeAndReasonRow, dimension: AnalyticsDeductionDimension): string =>
  dimension === "TYPE" ? formatTypeLabel(row) : formatGlCodeLabel(row);

const getRowId = (row: IDeductionsByTypeAndReasonRow, dimension: AnalyticsDeductionDimension): string => {
  if (dimension === "TYPE") {
    return String(row.deductionType!.id);
  }

  return String(row.glCode!.glCodeId);
};

const mapRow = (
  row: IDeductionsByTypeAndReasonRow,
  dimension: AnalyticsDeductionDimension,
  isOther: boolean
): DeductionByTypeTableRow => ({
  id: getRowId(row, dimension),
  label: getRowLabel(row, dimension),
  amount: row.amount,
  count: row.count,
  share: row.share,
  vsPrior: row.vsPrior,
  trend: row.trend,
  isOther,
});

const aggregateOtherRows = (rows: IDeductionsByTypeAndReasonRow[]): DeductionByTypeTableRow => ({
  id: OTHER_ROW_LABEL,
  label: OTHER_ROW_LABEL,
  amount: rows.reduce((total, row) => total + row.amount, 0),
  count: rows.reduce((total, row) => total + row.count, 0),
  share: rows.reduce((total, row) => total + row.share, 0),
  vsPrior: null,
  trend: null,
  isOther: true,
});

export const groupDeductionRowsWithOther = (
  rows: IDeductionsByTypeAndReasonRow[],
  dimension: AnalyticsDeductionDimension,
  maxRows: number = MAX_TOP_ANALYTICS_ROWS
): DeductionByTypeTableRow[] => {
  const unassignedRows = rows.filter(row => isUnassignedRow(row, dimension));
  const assignableRows = rows.filter(row => !isUnassignedRow(row, dimension));

  const eligible = assignableRows.filter(row => row.share >= MIN_ANALYTICS_SHARE_PERCENT);
  const belowThreshold = assignableRows.filter(row => row.share < MIN_ANALYTICS_SHARE_PERCENT);

  const sortedEligible = [...eligible].sort((firstRow, secondRow) => secondRow.amount - firstRow.amount);
  const topRows = sortedEligible.slice(0, maxRows);
  const overflowRows = sortedEligible.slice(maxRows);
  const othersSource = [...unassignedRows, ...overflowRows, ...belowThreshold];

  const mappedTopRows = topRows.map(row => mapRow(row, dimension, false));

  if (othersSource.length === 0) {
    return mappedTopRows;
  }

  return [...mappedTopRows, aggregateOtherRows(othersSource)];
};
